import { GroupNodeType, NodeType, QuestionType, VisibilityContextType, } from "./templateTypes";
import { jsonLogic } from "./json-logic";
/**
 * Recompute block visibility based on form answers.
 *
 * @param {object} params The parameters for computing the children block.
 * @param {Nodes} params.node The node to compute.
 * @param {Values} params.answers The answers to the form.
 * @param {Template} params.template The template of the form.
 * @param {Partial<{readOnly: boolean}>} [params.computed] The computed values.
 * @param {Record<string, boolean>} [params.preflightedAnswers] The pre-flighted answers.
 * @param {Nodes} [params.parentNode] The parent node.
 * @param {boolean} [forceVisible=false] Whether to force the block to be visible.
 * @returns {Blocks} The computed block.
 */
export const computeChildrenBlock = ({ node, answers, template, computed = { readOnly: false }, preflightedAnswers = {}, parentNode, }, forceVisible = false) => {
    const parent = isFieldArray(parentNode) ? parentNode : node;
    const state = {
        visible: isFieldArray(parentNode) || blockIsVisible(answers, node) || forceVisible,
    };
    if (isListNode(node)) {
        const { items, ...listNode } = node;
        return {
            ...listNode,
            state,
            items: items.map(item => ({
                ...item,
                children: item.children?.map(node => computeChildrenBlock({
                    node,
                    answers,
                    template,
                    computed,
                    preflightedAnswers,
                    parentNode: parent,
                }, forceVisible)),
            })),
        };
    }
    if (isFormControl(node)) {
        const { reference: refKey, ...formControlNode } = node;
        const reference = template.references[refKey];
        const fieldName = getFieldName(reference);
        return {
            ...formControlNode,
            state: {
                ...state,
                disabled: computed.readOnly ||
                    node.disabled === true ||
                    !!preflightedAnswers[fieldName],
                preFlight: !!preflightedAnswers[fieldName],
            },
            reference: isCheckboxReference(reference) || isRadioReference(reference)
                ? {
                    ...reference,
                    choices: reference.choices?.map(choice => computeChoiceChildrenBlock({
                        question: reference,
                        choice,
                        answers,
                        template,
                        computed,
                        preflightedAnswers,
                        parentNode: parent,
                    }, forceVisible)),
                }
                : reference,
        };
    }
    if (isMandatoryVideoNode(node)) {
        const { reference: refKey, ...video } = node;
        return {
            state,
            ...video,
            reference: template.references[refKey],
        };
    }
    if (isBranchNode(node)) {
        const { children, ...branchNode } = node;
        return {
            state: isFieldArray(node)
                ? {
                    // Note: Field array should be form-control, but is not so we must compute the state here
                    ...state,
                    disabled: computed.readOnly ||
                        node.disabled === true ||
                        !!preflightedAnswers[getFieldName(node)],
                    preFlight: !!preflightedAnswers[getFieldName(node)],
                }
                : state,
            ...branchNode,
            children: children.map(node => computeChildrenBlock({
                node,
                answers,
                template,
                computed,
                preflightedAnswers,
                parentNode: parent,
            }, forceVisible)),
        };
    }
    return { state, ...node };
};
/**
 * If parent is FieldArray, we make the field visible,
 * because it might be visible for some of the items
 * and we validate only visible fields.
 */
const isFieldArray = (node) => node?.type === NodeType.FIELD_ARRAY;
const isFormControl = (node) => node?.type === NodeType.FORM_CONTROL;
/**
 * Compute state for leaf fields in choice sub-form.
 * LIMITATION: Only leaf nodes are allowed here.
 */
const computeChoiceChildrenBlock = ({ question, answers, choice, template, computed = { readOnly: false }, preflightedAnswers = {}, parentNode, }, forceVisible) => {
    const { children } = choice;
    if (!children) {
        return choice;
    }
    return {
        ...choice,
        children: children.map(node => {
            const visible = isFieldArray(parentNode) ||
                (choiceIsActive(question, answers, choice) &&
                    blockIsVisible(answers, node)) ||
                forceVisible;
            const state = {
                visible,
            };
            if (isListNode(node)) {
                const { items, ...listNode } = node;
                return {
                    ...listNode,
                    state,
                    items: visible
                        ? items.map(item => ({
                            ...item,
                            children: item.children?.map(node => computeChildrenBlock({
                                node,
                                answers,
                                template,
                                computed,
                                preflightedAnswers,
                                parentNode,
                            }, forceVisible)),
                        }))
                        : [],
                };
            }
            if (isFormControl(node)) {
                const { reference: refKey, ...formControlNode } = node;
                const reference = template.references[refKey];
                const fieldName = getFieldName(reference);
                return {
                    ...formControlNode,
                    state: {
                        ...state,
                        disabled: computed.readOnly ||
                            node.disabled === true ||
                            !!preflightedAnswers[fieldName],
                        preFlight: !!preflightedAnswers[fieldName],
                    },
                    reference: isCheckboxReference(reference) || isRadioReference(reference)
                        ? {
                            ...reference,
                            choices: visible
                                ? reference.choices?.map(choice => computeChoiceChildrenBlock({
                                    question: reference,
                                    choice,
                                    answers,
                                    template,
                                    computed,
                                    preflightedAnswers,
                                    parentNode,
                                }, forceVisible))
                                : [],
                        }
                        : reference,
                };
            }
            if (isMandatoryVideoNode(node)) {
                const { reference: refKey, ...video } = node;
                return {
                    state,
                    ...video,
                    reference: template.references[refKey],
                };
            }
            if (isBranchNode(node)) {
                const { children, ...branchNode } = node;
                return {
                    state: isFieldArray(node)
                        ? {
                            // Note: Field array should be form-control, but is not so we must compute the state here
                            ...state,
                            disabled: computed.readOnly ||
                                node.disabled === true ||
                                !!preflightedAnswers[getFieldName(node)],
                            preFlight: !!preflightedAnswers[getFieldName(node)],
                        }
                        : state,
                    ...branchNode,
                    children: visible
                        ? children.map(node => computeChildrenBlock({
                            node,
                            answers,
                            template,
                            computed,
                            preflightedAnswers,
                            parentNode,
                        }, forceVisible))
                        : [],
                };
            }
            return { ...node, state };
        }),
    };
};
/**
 * Given question reference field and form values, check if the provided choice is active.
 */
export const choiceIsActive = (question, values, choice) => {
    const activeChoices = values[getFieldName(question)];
    if (isCheckboxReference(question) && Array.isArray(activeChoices)) {
        return activeChoices.includes(getFieldName(choice));
    }
    else if (isRadioReference(question) && typeof activeChoices === "string") {
        return activeChoices === getFieldName(choice);
    }
    else {
        return false;
    }
};
export const blockIsVisible = (values, block) => {
    if (typeof block.visible === "boolean") {
        return block.visible;
    }
    if (block.visible instanceof Object) {
        return jsonLogic.apply(block.visible, values);
    }
    return true;
};
export const getFieldName = (item) => item.id;
export const isResidentExperienceContextNode = (node) => node.type !== "pdf" &&
    (!node.visibilityContext ||
        node.visibilityContext.includes(VisibilityContextType.RE));
export const isPdfContextNode = (node) => node.type === "pdf" ||
    node.visibilityContext?.includes(VisibilityContextType.PDF);
export const isSlideNode = (node) => node.type === GroupNodeType.SLIDE;
export const isTableOfContentNode = (node) => node.type === NodeType.TABLE_OF_CONTENT;
export const isMandatoryVideoNode = (node) => node.type === NodeType.MANDATORY_VIDEO;
export const isBranchNode = (node) => node.children;
export const isQueryChoicesNode = (node) => node.query;
export const isStaticChoicesNode = (node) => node.choices;
export const isListNode = (node) => node.type === NodeType.LIST;
export const isSelectReference = (reference) => reference.type === QuestionType.SELECT;
export const isRadioReference = (reference) => reference.type === QuestionType.RADIO;
export const isCheckboxReference = (reference) => reference.type === QuestionType.CHECKBOX;
export const isSignatureReference = (reference) => reference.type === QuestionType.SIGNATURE;
/**
 * Extracts the slides from a template.
 *
 * @param {Template} template The template to extract the slides from.
 * @param {Values} answers The answers to the form.
 * @returns {any[]} The extracted slides.
 */
export const extractTemplateSlides = (template, answers) => template.children.flatMap(sectionGroup => sectionGroup.children.flatMap(section => {
    const isSectionVisible = blockIsVisible(answers, section);
    return section.children.filter(isSlideNode).flatMap(slide => {
        return {
            ...slide,
            children: slide.children.map(node => computeChildrenBlock({
                node,
                answers,
                template,
            })),
            state: {
                visible: isSectionVisible && blockIsVisible(answers, slide),
            },
        };
    });
}));
/**
 * Get array of visible template nodes - slides or pdf elements.
 *
 * @param {Template} template The template to extract the nodes from.
 * @param {Values} values The values of the form.
 * @param {boolean} [forceVisible=false] Whether to force the nodes to be visible.
 * @returns {ReadonlyArray<Slide | PdfNode>} The visible template nodes.
 */
export const extractVisibleTemplateNodes = (template, values, forceVisible = false) => template.children.flatMap(sectionGroup => sectionGroup.children.flatMap(section => {
    const sectionIsVisible = blockIsVisible(values, section) || forceVisible;
    return section.children.filter(slide => sectionIsVisible &&
        !slide.afterAdmission &&
        (blockIsVisible(values, slide) || forceVisible));
}));
/**
 * Get array of visible after admission template nodes - slides or pdf elements.
 *
 * @param {Template} template The template to extract the nodes from.
 * @param {Values} values The values of the form.
 * @param {boolean} [forceVisible=false] Whether to force the nodes to be visible.
 * @returns {ReadonlyArray<Slide | PdfNode>} The visible after admission template nodes.
 */
export const extractVisibleAfterAdmissionTemplateNodes = (template, values, forceVisible = false) => template.children.flatMap(sectionGroup => sectionGroup.children.flatMap(section => {
    const sectionIsVisible = blockIsVisible(values, section) || forceVisible;
    return section.children.filter(slide => sectionIsVisible &&
        slide.afterAdmission &&
        (blockIsVisible(values, slide) || forceVisible));
}));
